import { createRoute, Link } from '@tanstack/react-router';
import {
  ActionMenu,
  Button,
  DefinitionList,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Mono,
  PageHeader,
  ResourceTabs,
  SectionCard,
  Select,
  StatusBadge,
  TextInput,
} from '@verydia/ui';
import type { AuditEventResource, ProductResource } from '@verydia/contracts';
import { VerydiaApiError } from '@verydia/sdk';
import { Archive, History, Pencil, RotateCcw } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { rootRoute } from './root.tsx';
import { describeFailure, isStarting } from '../lib/api.ts';
import { useConsoleContext } from '../lib/console-context.tsx';
import {
  useArchiveProduct,
  useProduct,
  useProductActivity,
  useUpdateProduct,
} from '../lib/identity-queries.ts';
import { DISTRIBUTIONS, distributionLabel } from '../components/distribution.ts';
import { CopyableId } from '../components/copyable-id.tsx';
import { EnvironmentsPanel } from '../components/environments.tsx';
import { PlansPanel } from '../components/plans.tsx';

const STATUS_TONE = { ACTIVE: 'success', ARCHIVED: 'neutral' } as const;
const STATUS_LABEL = { ACTIVE: 'Active', ARCHIVED: 'Archived' } as const;

/** True when the server refused this write because someone else already saved. */
function isConflict(error: unknown): boolean {
  return error instanceof VerydiaApiError && error.code === 'resource_conflict';
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** `PRODUCT_UPDATED` is a wire value; "Product updated" is a sentence. */
function humaniseAction(action: string): string {
  const words = action.toLowerCase().replaceAll('_', ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/* ------------------------------- editing ---------------------------------- */

/**
 * Editing a product, carrying the version it was loaded at.
 *
 * The version travels with the edit so a stale tab is refused rather than
 * silently winning. When that happens the operator is told what actually
 * happened and given a way forward — not a status code, and never a quiet
 * overwrite of somebody else's work.
 */
function EditProductDialog({
  organizationId,
  product,
  open,
  onOpenChange,
  onReload,
}: {
  organizationId: string;
  product: ProductResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReload: () => void;
}) {
  const [name, setName] = useState(product.name);
  const [distribution, setDistribution] = useState<string>(product.distribution);
  const [description, setDescription] = useState(product.description ?? '');
  const update = useUpdateProduct(organizationId, product.id);
  const failure = update.isError ? describeFailure(update.error) : null;
  const conflict = isConflict(update.error);

  const close = () => {
    update.reset();
    setName(product.name);
    setDistribution(product.distribution);
    setDescription(product.description ?? '');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={conflict ? 'This product changed since you opened it' : `Edit ${product.name}`}
      description={
        conflict
          ? 'Someone else saved a change while this page was open. Your edit was not applied, and theirs was kept.'
          : 'Changes are recorded in the audit log.'
      }
      footer={
        conflict ? (
          <>
            <Button onClick={close}>Discard my change</Button>
            <Button
              variant="primary"
              icon={<RotateCcw />}
              onClick={() => {
                update.reset();
                onReload();
              }}
            >
              Reload latest version
            </Button>
          </>
        ) : (
          <>
            <Button onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={update.isPending || name.trim() === ''}
              onClick={() =>
                update.mutate(
                  {
                    expected_version: product.version,
                    name: name.trim(),
                    distribution,
                    description: description.trim() === '' ? null : description.trim(),
                  },
                  { onSuccess: close },
                )
              }
            >
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </>
        )
      }
    >
      {conflict ? (
        <p className="text-[13px] text-[var(--vc-text-muted)]">
          Reload to see the current version, then reapply your change if it is still needed.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(event) => event.preventDefault()}>
          <Field label="Product name" required>
            {(props) => (
              <TextInput
                {...props}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          {/*
            Read-only rather than absent, and read-only rather than disabled.

            Absent would leave an operator wondering where it went; disabled
            reads as "you are not allowed", which is a different and wrong
            explanation — nobody is allowed, including the owner. The API
            refuses a slug change outright (422 immutable_field), so this is
            not a form-only rule.
          */}
          <Field
            label="Slug"
            description="Permanent. It appears in URLs, API paths and integrations, which cannot follow a rename."
          >
            {(props) => (
              <TextInput
                {...props}
                readOnly
                value={product.slug}
                className="bg-[var(--vc-surface-sunken)] text-[var(--vc-text-muted)]"
              />
            )}
          </Field>

          <Field label="Distribution" required description="How this software is delivered or run.">
            {(props) => (
              <Select
                {...props}
                value={distribution}
                onChange={(event) => setDistribution(event.target.value)}
              >
                {DISTRIBUTIONS.map((option) => (
                  <option key={option} value={option}>
                    {distributionLabel(option)}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Description"
            description="Optional. A short, factual summary of what this product is."
            error={failure !== null ? failure.message : undefined}
          >
            {(props) => (
              <TextInput
                {...props}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </Field>

          {failure?.requestId !== null && failure?.requestId !== undefined ? (
            <p className="text-[11.5px] text-[var(--vc-text-subtle)]">
              Request <Mono>{failure.requestId}</Mono>
            </p>
          ) : null}
        </form>
      )}
    </Dialog>
  );
}

/* ------------------------------- archiving -------------------------------- */

/**
 * Archiving is not deleting, and the copy says so.
 *
 * Nothing is destroyed: the row stays, its history stays, and the route keeps
 * working. What changes is that the product leaves the active catalog. Naming
 * this "Delete" — or drawing it with a bin — would describe an operation the
 * system does not perform, and would make an operator hesitate over a
 * reversible act or, worse, expect their data to be gone when it is not.
 */
function ArchiveProductDialog({
  organizationId,
  product,
  open,
  onOpenChange,
}: {
  organizationId: string;
  product: ProductResource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const archive = useArchiveProduct(organizationId, product.id);
  const failure = archive.isError ? describeFailure(archive.error) : null;

  const close = () => {
    archive.reset();
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={`Archive ${product.name}?`}
      description="Archived products are removed from normal active catalog selection. Historical records remain preserved."
      footer={
        <>
          <Button onClick={close} disabled={archive.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            icon={<Archive />}
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate({ expected_version: product.version }, { onSuccess: close })
            }
          >
            {archive.isPending ? 'Archiving…' : 'Archive product'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-[13px] text-[var(--vc-text-muted)]">
        <p>
          This product stays readable at its current address, and its audit history is kept exactly
          as it is. It stops appearing where an active product is expected.
        </p>
        {failure !== null ? (
          <p role="alert" className="text-[var(--vc-danger-text)]">
            {failure.message}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/* -------------------------------- activity -------------------------------- */

/**
 * This product's own history.
 *
 * Scoped on the server to (organization, product), so an organization-level
 * event — a member invited, the organization created — cannot appear here and
 * be read as something that happened to this product.
 */
function ProductActivity({
  organizationId,
  productId,
  productName,
}: {
  organizationId: string;
  productId: string;
  productName: string;
}) {
  const activity = useProductActivity(organizationId, productId);
  const failure = activity.isError ? describeFailure(activity.error) : null;
  const starting = activity.isFetching && isStarting(activity.failureReason ?? activity.error);
  const events: readonly AuditEventResource[] = activity.data?.data.data ?? [];

  return (
    <SectionCard title="Recent activity" description="Changes recorded against this product.">
      {starting ? (
        <LoadingState label="Verydia is starting up" />
      ) : activity.isPending ? (
        <LoadingState label="Loading activity" />
      ) : failure !== null ? (
        <ErrorState
          title="Could not load this product’s activity"
          description={failure.message}
          requestId={failure.requestId}
          onRetry={() => void activity.refetch()}
        />
      ) : events.length === 0 ? (
        <EmptyState
          icon={<History />}
          title="No activity yet"
          description={`Nothing has been recorded against ${productName} since it was created.`}
        />
      ) : (
        <ul className="flex flex-col">
          {events.map((event) => (
            <li
              key={event.id}
              className="flex items-baseline justify-between gap-4 border-b border-[var(--vc-border)] py-2.5 last:border-b-0"
            >
              <span className="text-[13px] font-medium text-[var(--vc-text)]">
                {humaniseAction(event.action)}
              </span>
              <span className="shrink-0 text-[12px] tabular-nums text-[var(--vc-text-muted)]">
                {formatTimestamp(event.occurred_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

/* --------------------------------- route ---------------------------------- */

/**
 * The product resource route.
 *
 * The id in the URL is the identity, not the selected context: this page loads
 * from the address, so a bookmark, a hard refresh and a pasted link all work
 * without having visited the list first. Context follows the route rather than
 * the other way round.
 */
/**
 * The chrome every product tab shares: breadcrumbs, header, actions, tabs, and
 * the four load states. Extracted so the Environments tab is a real route
 * rather than a second page that has to remember to look the same.
 */
export function ProductShell({
  productId,
  tab,
  children,
}: {
  productId: string;
  tab: 'overview' | 'environments' | 'plans';
  children: (product: ProductResource, organizationId: string) => ReactNode;
}) {
  const context = useConsoleContext();
  const organizationId = context.organization?.id ?? null;
  const product = useProduct(organizationId, productId);

  const failure = product.isError ? describeFailure(product.error) : null;
  const starting = product.isFetching && isStarting(product.failureReason ?? product.error);
  const data = product.data?.data;
  const [editOpen, setEditOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);

  /*
    The route selects the product, not the other way round.

    Opening this address makes this product the working context, so the
    selector agrees with the page instead of contradicting it. It runs only
    once the product has actually loaded: selecting from the URL parameter
    alone would put an unverified — possibly foreign, possibly nonexistent — id
    into context before the API had said whether it may be read at all.
  */
  const selectProduct = context.selectProduct;
  const selectedProductId = context.product?.id ?? null;
  useEffect(() => {
    if (data === undefined) return;
    if (selectedProductId === data.id) return;
    // Archived products need no special case here: the selector resolves
    // against the ACTIVE catalog, so an archived id simply never becomes a
    // selection. A second check in this file would look like the rule and would
    // not be it — and a guard that cannot fail is one nobody maintains.
    selectProduct(data.id);
  }, [data, selectedProductId, selectProduct]);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={data?.name ?? 'Product'}
        description={data?.description ?? undefined}
        breadcrumbs={[
          { label: context.organization?.name ?? 'Organization', href: '/' },
          { label: 'Products', href: '/products' },
          { label: data?.name ?? 'Product' },
        ]}
        linkComponent={Link}
        meta={
          data ? (
            <>
              <StatusBadge tone={STATUS_TONE[data.status]}>{STATUS_LABEL[data.status]}</StatusBadge>
              <span className="text-[12.5px] text-[var(--vc-text-muted)]">
                {distributionLabel(data.distribution)}
              </span>
            </>
          ) : null
        }
        actions={
          data && data.status === 'ACTIVE' ? (
            <>
              <Button icon={<Pencil />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
              <ActionMenu
                label={`More actions for ${data.name}`}
                items={[
                  {
                    key: 'archive',
                    label: 'Archive product',
                    icon: <Archive />,
                    destructive: true,
                    onSelect: () => setArchiveOpen(true),
                  },
                ]}
              />
            </>
          ) : null
        }
      />

      <ResourceTabs
        label="Product sections"
        linkComponent={Link}
        tabs={[
          {
            key: 'overview',
            label: 'Overview',
            href: `/products/${productId}`,
            current: tab === 'overview',
          },
          {
            key: 'environments',
            label: 'Environments',
            href: `/products/${productId}/environments`,
            current: tab === 'environments',
          },
          {
            key: 'plans',
            label: 'Licensed product plans',
            href: `/products/${productId}/plans`,
            current: tab === 'plans',
          },
        ]}
      />

      {starting ? (
        <SectionCard>
          <LoadingState label="Verydia is starting up" />
        </SectionCard>
      ) : product.isPending ? (
        <SectionCard>
          <LoadingState label="Loading product" />
        </SectionCard>
      ) : failure !== null || data === undefined ? (
        <SectionCard>
          <ErrorState
            title={
              product.error instanceof VerydiaApiError && product.error.status === 404
                ? 'No such product'
                : 'Could not load this product'
            }
            description={
              product.error instanceof VerydiaApiError && product.error.status === 404
                ? 'This product does not exist, or it belongs to an organization you are not a member of.'
                : (failure?.message ?? 'An unexpected error occurred.')
            }
            requestId={failure?.requestId ?? null}
            onRetry={() => void product.refetch()}
          />
        </SectionCard>
      ) : (
        children(data, organizationId!)
      )}

      {data !== undefined && organizationId !== null ? (
        <>
          <EditProductDialog
            key={`edit-${data.version}`}
            organizationId={organizationId}
            product={data}
            open={editOpen}
            onOpenChange={setEditOpen}
            onReload={() => void product.refetch()}
          />
          <ArchiveProductDialog
            key={`archive-${data.version}`}
            organizationId={organizationId}
            product={data}
            open={archiveOpen}
            onOpenChange={setArchiveOpen}
          />
        </>
      ) : null}
    </div>
  );
}

/** The Overview tab: what the product is, what it has, and what has happened to it. */
function ProductDetailPage() {
  const { productId } = productDetailRoute.useParams();
  const context = useConsoleContext();

  return (
    <ProductShell productId={productId} tab="overview">
      {(data, organizationId) => (
        <>
          <SectionCard
            title="Product details"
            description="What this product is, as recorded in the catalog."
          >
            <DefinitionList
              items={[
                { label: 'Name', value: data.name },
                { label: 'Slug', value: <Mono>{data.slug}</Mono> },
                { label: 'Distribution', value: distributionLabel(data.distribution) },
                { label: 'Status', value: STATUS_LABEL[data.status] },
                { label: 'Created', value: formatTimestamp(data.created_at) },
                { label: 'Updated', value: formatTimestamp(data.updated_at) },
              ]}
            />
          </SectionCard>

          {/*
            Real counts from the product resource itself.

            Nothing here reports customers, licences or validations. A literal
            zero would claim the feature exists and has no rows, which is a
            different and untrue statement from "this does not exist yet".
          */}
          <SectionCard title="Catalog" description="What has been defined for this product so far.">
            <DefinitionList
              items={[
                { label: 'Environments', value: String(data.environment_count) },
                {
                  label: 'Licensed product plans',
                  value: String(data.licensed_product_plan_count),
                },
              ]}
            />
          </SectionCard>

          <ProductActivity
            organizationId={organizationId!}
            productId={data.id}
            productName={data.name}
          />

          {/*
            Identifiers and the concurrency version, kept apart from the
            business facts above. `version` is machinery: an operator does not
            need to understand row versions to edit successfully, because the
            form carries the version for them and the server refuses a stale
            one. It is here because it is genuinely useful when reading an API
            response or a support ticket — not because it is product metadata.
          */}
          <SectionCard
            title="Technical details"
            description="Identifiers for API calls, integrations and support."
          >
            <DefinitionList
              items={[
                { label: 'Product ID', value: <CopyableId value={data.id} label="product id" /> },
                {
                  label: 'Organization',
                  value: context.organization?.name ?? data.organization_id,
                },
                { label: 'Record version', value: String(data.version) },
              ]}
            />
          </SectionCard>
        </>
      )}
    </ProductShell>
  );
}

/** The Environments tab. Same shell, same product, a different section. */
function ProductEnvironmentsPage() {
  const { productId } = productEnvironmentsRoute.useParams();

  return (
    <ProductShell productId={productId} tab="environments">
      {(data, organizationId) => (
        <EnvironmentsPanel
          organizationId={organizationId}
          productId={data.id}
          productName={data.name}
          productIsActive={data.status === 'ACTIVE'}
        />
      )}
    </ProductShell>
  );
}

export const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productId',
  component: ProductDetailPage,
});

/** The Licensed product plans tab. Same shell, a different section. */
function ProductPlansPage() {
  const { productId } = productPlansRoute.useParams();

  return (
    <ProductShell productId={productId} tab="plans">
      {(data, organizationId) => (
        <PlansPanel
          organizationId={organizationId}
          productId={data.id}
          productName={data.name}
          productIsActive={data.status === 'ACTIVE'}
        />
      )}
    </ProductShell>
  );
}

export const productPlansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productId/plans',
  component: ProductPlansPage,
});

export const productEnvironmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$productId/environments',
  component: ProductEnvironmentsPage,
});
